import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Star, CheckCircle, ArrowRight, Shield, Award, Users, Heart } from 'lucide-react';
import { siteInfo } from '../data/mockData';

const FunerariaNaTijuca = () => {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "FuneralHome",
    "name": "Santa Casa Serviço Funerário Imediato - Funerária na Tijuca",
    "description": "Funerária 24 horas na Tijuca, Rio de Janeiro. Serviços funerários completos com cremação, sepultamento e velório.",
    "url": "https://funerariasantacasaassistencial.com/funeraria-na-tijuca",
    "telephone": "+552141064022",
    "priceRange": "R$ 1.599 - R$ 4.599",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Rua Conde de Bonfim, 300",
      "addressLocality": "Tijuca",
      "addressRegion": "RJ",
      "addressCountry": "BR"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "areaServed": "Tijuca"
  };

  return (
    <div className="min-h-screen">
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      {/* Hero Ultra-Otimizado */}
      <section className="relative bg-gradient-to-br from-teal-600 via-teal-500 to-lime-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Schema Breadcrumb */}
            <nav className="mb-6 text-sm text-teal-100">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-2">/</span>
              <span>Funerária na Tijuca</span>
            </nav>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Funerária na Tijuca
            </h1>
            <p className="text-2xl md:text-3xl mb-4 text-teal-50">
              Santa Casa Serviço Funerário Imediato - A Melhor Funerária na Tijuca RJ
            </p>
            <p className="text-xl mb-8 max-w-4xl mx-auto">
              Atendimento 24 horas | Cremação | Translado | Sepultamento | Velório<br/>
              Rua Conde de Bonfim, 300 - Tijuca, Rio de Janeiro - RJ
            </p>

            {/* CTAs Principais */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href={`tel:${siteInfo.phone.replace(/\D/g, '')}`}
                className="inline-flex items-center justify-center bg-lime-400 hover:bg-lime-500 text-gray-900 px-6 py-3 sm:px-10 sm:py-5 rounded-2xl text-sm sm:text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105 animate-pulse"
              >
                <Phone className="w-4 h-4 sm:w-6 sm:h-6 mr-2" />
                LIGAR AGORA: (21) 4106-4022
              </a>
              <a
                href="https://wa.me/552141064022?text=Ol%C3%A1!%20Vim%20pelo%20site,%20gostaria%20de%20informa%C3%A7%C3%B5es"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-6 py-3 sm:px-10 sm:py-5 rounded-2xl text-sm sm:text-lg font-bold border-2 border-white/40 transition-all duration-300"
              >
                <svg className="w-4 h-4 sm:w-6 sm:h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp Funerária Tijuca
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/30">
                <Star className="w-5 h-5 text-yellow-300 inline mr-2" />
                <span className="font-bold">4.8/5 ⭐⭐⭐⭐⭐</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/30">
                <Users className="w-5 h-5 inline mr-2" />
                <span className="font-bold">18.000+ Famílias</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/30">
                <Clock className="w-5 h-5 inline mr-2" />
                <span className="font-bold">24h Disponível</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Por Que Somos a Melhor Funerária na Tijuca */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-12">
            Por Que Somos a Melhor Funerária na Tijuca?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-lime-50 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-lime-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Localização Estratégica</h3>
              <p className="text-gray-700">
                <strong>Funerária na Tijuca</strong>, fácil acesso de toda Zona Norte do RJ
              </p>
            </div>

            <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-lime-50 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-lime-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Atendimento 24 Horas</h3>
              <p className="text-gray-700">
                <strong>Funerária 24h na Tijuca</strong>, sempre disponível quando você mais precisa
              </p>
            </div>

            <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-lime-50 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-lime-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Experiência Comprovada</h3>
              <p className="text-gray-700">
                Mais de <strong>18.000 famílias atendidas</strong> pela funerária na Tijuca
              </p>
            </div>

            <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-lime-50 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-lime-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Atendimento Humanizado</h3>
              <p className="text-gray-700">
                <strong>Funerária com apoio emocional</strong> e suporte integral no Tijuca
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Serviços da Funerária na Tijuca */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-4">
            Serviços da Funerária na Tijuca
          </h2>
          <p className="text-center text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            A <strong>Santa Casa Serviço Funerário Imediato</strong> é a <strong>funerária mais completa do Tijuca</strong>, 
            oferecendo todos os serviços funerários que sua família precisa.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Cremação no Tijuca',
                desc: 'Serviços completos de cremação realizados por nossa funerária na Tijuca com toda documentação',
                icon: '🕯️'
              },
              {
                title: 'Sepultamento no Tijuca',
                desc: 'Organização completa de sepultamento pela melhor funerária do Tijuca',
                icon: '⚱️'
              },
              {
                title: 'Translado Funerário',
                desc: 'Funerária na Tijuca especializada em translado nacional e internacional',
                icon: '✈️'
              },
              {
                title: 'Velório 24h na Tijuca',
                desc: 'Estrutura completa para velório oferecida pela funerária na Tijuca',
                icon: '🕊️'
              },
              {
                title: 'Tanatopraxia',
                desc: 'Preparação profissional do corpo pela funerária mais qualificada do Tijuca',
                icon: '💐'
              },
              {
                title: 'Planos Funerários Tijuca',
                desc: 'Planos funerários exclusivos da funerária na Tijuca para sua tranquilidade',
                icon: '📋'
              }
            ].map((servico, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all hover:-translate-y-1">
                <div className="text-5xl mb-4">{servico.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{servico.title}</h3>
                <p className="text-gray-700">{servico.desc}</p>
                <Link to="/contato" className="inline-flex items-center text-teal-600 font-semibold mt-4 hover:text-lime-600">
                  Solicitar serviço <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Ultra-Otimizado para "funerária no tijuca" */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Perguntas Frequentes sobre Funerária na Tijuca
          </h2>
          <div className="space-y-6">
            {[
              {
                q: 'Qual a melhor funerária na Tijuca?',
                a: 'A <strong>Santa Casa Serviço Funerário Imediato</strong> é considerada a melhor <strong>funerária na Tijuca</strong> por oferecer atendimento 24 horas, ter mais de 18.000 famílias atendidas, localização estratégica na Rua Conde de Bonfim, 300, e serviços completos de cremação, sepultamento e translado. Nossa <strong>funerária na Tijuca</strong> tem avaliação 4.8/5 estrelas.'
              },
              {
                q: 'Onde fica a funerária na Tijuca?',
                a: 'Nossa <strong>funerária fica no Tijuca</strong>, mais especificamente na <strong>Rua Conde de Bonfim, 300, Tijuca, Rio de Janeiro - RJ, CEP 20775-120</strong>. A <strong>funerária está localizada na Tijuca</strong>, com fácil acesso pelo Metrô Tijuca e Trem Tijuca. Telefone da funerária na Tijuca: (21) 4106-4022.'
              },
              {
                q: 'Funerária na Tijuca funciona 24 horas?',
                a: 'Sim! Nossa <strong>funerária na Tijuca funciona 24 horas por dia</strong>, todos os dias da semana, incluindo finais de semana e feriados. A <strong>funerária 24h na Tijuca</strong> está sempre disponível para atender sua família quando mais precisa. Ligue agora: (21) 4106-4022.'
              },
              {
                q: 'Quanto custa uma funerária na Tijuca?',
                a: 'Os valores da <strong>funerária na Tijuca</strong> variam conforme o tipo de serviço (cremação, sepultamento, translado), urna escolhida e outros itens. Nossa <strong>funerária na Tijuca</strong> trabalha com total transparência nos preços. Entre em contato com nossa <strong>funerária na Tijuca</strong> para um orçamento personalizado sem compromisso pelo telefone (21) 4106-4022.'
              },
              {
                q: 'A funerária na Tijuca faz cremação?',
                a: 'Sim! Nossa <strong>funerária na Tijuca faz cremação completa</strong>, incluindo toda documentação necessária, coordenação da cerimônia, processo em crematórios certificados e diversas opções de urnas. Somos especialistas em <strong>cremação no Tijuca</strong> e Zona Norte do Rio de Janeiro. <strong>Cremação 24h na Tijuca</strong>: (21) 4106-4022.'
              },
              {
                q: 'Funerária na Tijuca faz translado?',
                a: 'Sim! Nossa <strong>funerária na Tijuca realiza translado nacional e internacional</strong>. Cuidamos de toda documentação, logística e protocolos para o transporte seguro. A <strong>funerária na Tijuca especializada em translado</strong> oferece serviço completo para qualquer cidade do Brasil ou exterior.'
              },
              {
                q: 'Como contratar funerária na Tijuca?',
                a: 'Para contratar nossa <strong>funerária na Tijuca</strong>, basta ligar no telefone <strong>(21) 4106-4022</strong> (24h) ou enviar WhatsApp. Nossa <strong>funerária na Tijuca</strong> oferece atendimento imediato. Você também pode visitar nossa <strong>funerária na Tijuca</strong> na Rua Conde de Bonfim, 300.'
              },
              {
                q: 'Qual o telefone da funerária na Tijuca?',
                a: 'O telefone da <strong>funerária na Tijuca</strong> é <strong>(21) 4106-4022</strong>. Atendimento 24 horas da <strong>funerária na Tijuca</strong>. Você também pode entrar em contato com nossa <strong>funerária na Tijuca</strong> pelo WhatsApp. A <strong>funerária na Tijuca</strong> está sempre disponível para atendê-lo.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{faq.q}</h3>
                <p className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.a }}></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Depoimentos Funerária na Tijuca */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            O Que Dizem Sobre Nossa Funerária na Tijuca
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                nome: 'Maria Silva',
                texto: 'Melhor funerária na Tijuca! Atendimento humanizado e respeitoso. A funerária na Tijuca nos ajudou em um momento muito difícil.',
                rating: 5
              },
              {
                nome: 'João Santos',
                texto: 'Precisava de uma funerária na Tijuca com urgência. Foram super rápidos e atenciosos. Recomendo essa funerária na Tijuca!',
                rating: 5
              },
              {
                nome: 'Ana Costa',
                texto: 'Excelente funerária na Tijuca. Fizeram toda a cremação com muito respeito. A melhor funerária do Tijuca sem dúvidas!',
                rating: 5
              }
            ].map((depoimento, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(depoimento.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">\"{depoimento.texto}\"</p>
                <p className="font-bold text-gray-900">{depoimento.nome}</p>
                <p className="text-sm text-gray-500">Cliente Verificado - Funerária na Tijuca</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Seção de Atendimento Funerário Detalhado */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Atendimento Funerário Completo na Tijuca
          </h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg mb-8">
              Serviço de atendimento funerário 24 horas na Tijuca com equipe especializada, remoção imediata, documentação completa e suporte integral. Atendemos toda região da Tijuca, Vila Isabel, Grajaú, Maracanã e adjacências com rapidez e profissionalismo.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Final Ultra-Otimizado */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-lime-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Precisa de Uma Funerária na Tijuca Agora?
          </h2>
          <p className="text-2xl mb-8 text-teal-50">
            A Melhor Funerária na Tijuca Está Pronta Para Atendê-lo 24 Horas
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${siteInfo.phone.replace(/\D/g, '')}`}
              className="inline-flex items-center justify-center bg-white text-teal-600 hover:bg-gray-100 px-6 py-3 rounded-xl text-base font-bold shadow-xl transition-all duration-300 hover:scale-105"
            >
              <Phone className="w-5 h-5 mr-2" />
              Ligar: (21) 4106-4022
            </a>
            <Link
              to="/contato"
              className="inline-flex items-center justify-center bg-lime-500 hover:bg-lime-600 text-white px-6 py-3 rounded-xl text-base font-bold transition-all duration-300 hover:scale-105"
            >
              Fale Conosco
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Conteúdo SEO Rico */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Funerária na Tijuca - Santa Casa Serviço Funerário Imediato
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-gray-700">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">A Melhor Funerária na Tijuca RJ</h3>
                <p className="mb-4">
                  A <strong>Santa Casa Serviço Funerário Imediato</strong> é reconhecida como a melhor <strong>funerária na Tijuca</strong> e 
                  em toda a <strong>Zona Norte do Rio de Janeiro</strong>. Nossa <strong>funerária localizada no Tijuca</strong> oferece 
                  atendimento 24 horas com equipe especializada em todos os serviços funerários.
                </p>
                <p className="mb-4">
                  Como <strong>funerária na Tijuca</strong>, estamos estrategicamente localizados na 
                  <strong> Rua Conde de Bonfim, 300, Tijuca - RJ</strong>, facilitando o acesso de toda a região. 
                  Nossa <strong>funerária 24h na Tijuca</strong> já atendeu mais de 18.000 famílias com excelência.
                </p>
                <p className="mb-4">
                  Quando você procura por "<strong>funerária no tijuca</strong>", encontra nossa empresa como referência pela 
                  qualidade do atendimento, infraestrutura completa e equipe humanizada. Somos a <strong>funerária de confiança 
                  do Tijuca</strong> há anos.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Serviços da Funerária na Tijuca</h3>
                <p className="mb-4">
                  Nossa <strong>funerária na Tijuca</strong> oferece serviços completos:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li><strong>Cremação no Tijuca</strong> - Funerária especializada em cremação 24h</li>
                  <li><strong>Sepultamento no Tijuca</strong> - Funerária com cerimônias completas</li>
                  <li><strong>Translado Funerário</strong> - Funerária na Tijuca para nacional/internacional</li>
                  <li><strong>Velório 24h</strong> - Funerária na Tijuca com estrutura completa</li>
                  <li><strong>Planos Funerários</strong> - Funerária na Tijuca com planos acessíveis</li>
                </ul>
                <p className="mb-4">
                  Como <strong>funerária 24 horas no Tijuca</strong>, estamos sempre prontos para atender sua família 
                  com agilidade e respeito. Nossa <strong>funerária na Tijuca RJ</strong> é referência em atendimento 
                  humanizado e serviços de qualidade.
                </p>
              </div>
            </div>

            <div className="mt-8 bg-gradient-to-br from-teal-50 to-lime-50 rounded-2xl p-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                Por Que Escolher Nossa Funerária na Tijuca?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                  <div className="text-5xl font-bold text-teal-600 mb-2">24h</div>
                  <p className="font-semibold">Funerária 24h na Tijuca</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                  <div className="text-5xl font-bold text-teal-600 mb-2">18k+</div>
                  <p className="font-semibold">Famílias Atendidas pela Funerária</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                  <div className="text-5xl font-bold text-teal-600 mb-2">4.8★</div>
                  <p className="font-semibold">Avaliação da Funerária na Tijuca</p>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-gray-900 text-white rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Contato da Funerária na Tijuca</h3>
              <p className="text-xl mb-4">
                <strong>Funerária Santa Casa Serviço Funerário Imediato - Tijuca, Rio de Janeiro</strong>
              </p>
              <p className="mb-2">📍 Endereço da Funerária: Rua Conde de Bonfim, 300 - Tijuca, RJ - CEP 20775-120</p>
              <p className="mb-2">📞 Telefone da Funerária na Tijuca: <a href="tel:2141064022" className="text-lime-400 font-bold">(21) 4106-4022</a></p>
              <p className="mb-4">🕒 Horário: <strong>Funerária 24 horas no Tijuca</strong> - Todos os dias</p>
              <p className="text-lime-300 font-bold">
                ⭐ A MELHOR FUNERÁRIA NO MEIER - ATENDIMENTO IMEDIATO ⭐
              </p>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default FunerariaNaTijuca;
